import { useState, useEffect, useCallback } from 'react';
import { getExploreVenuesV1 } from '@/services/platform/api/explore/explore';
import type { GetExploreVenuesV1Params } from '@/services/platform/api/model';
import type { VenueGroupDto } from '@/services/platform/api/model';

export interface ExploreVenuesState {
  venues: VenueGroupDto[];
  isLoading: boolean;
  refetch: () => void;
}

export function useExploreVenues(
  params?: GetExploreVenuesV1Params,
  options?: { enabled?: boolean }
): ExploreVenuesState {
  const [venues, setVenues] = useState<VenueGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const enabled = options?.enabled ?? true;

  const fetch = useCallback(
    async (isStale: () => boolean) => {
      if (!enabled) return;
      setIsLoading(true);
      try {
        const response = await getExploreVenuesV1(params);
        if (isStale()) return;
        if (response.status === 200) {
          setVenues(response.data);
        }
      } catch {
        // silent — caller checks venues === []
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    },
    [enabled, params?.locationNodeKey, params?.serviceCategoryNodeKey, params?.venueGroupNodeKey]
  );

  useEffect(() => {
    let stale = false;
    fetch(() => stale);
    return () => {
      stale = true;
    };
  }, [fetch]);

  return { venues, isLoading, refetch: () => fetch(() => false) };
}
